import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';

/**
 * PD-16674 — what a payment link lets its holder do.
 *
 *  - `invoice`         pay one invoice (in full or in part);
 *  - `statement`       pay the open invoices a statement listed;
 *  - `payment_method`  save a card or bank account to the client's account
 *                      without paying anything, so staff never have to take
 *                      card numbers over the phone.
 *
 * Stored as a varchar rather than a database enum so a later link type needs
 * no column alter.
 */
export type PaymentLinkType = 'invoice' | 'statement' | 'payment_method';

/**
 * PD-16674 — how a link came to exist. Recorded for the history staff see and
 * for support questions ("which email did this link come from?").
 */
export type PaymentLinkSource =
	/** Staff copied it from the invoice or client page. */
	| 'copy'
	/** Staff emailed it with the "Email payment link" action. */
	| 'email'
	/** The Pay button in the invoice email (manual send or fulfillment). */
	| 'invoice_email'
	/** The Pay button in the self-delivery receipt. */
	| 'delivery_receipt'
	/** The "Pay your balance" button in a statement email. */
	| 'statement_email';

/**
 * PD-16674 — a link that lets someone without a storefront login pay an
 * invoice or a statement, or save a payment method, from a page on the
 * tenant's storefront.
 *
 * Security model (see PaymentLinkIssuerService / PublicPaymentLinksService):
 *
 *  - The token itself is never stored. Only a keyed hash of it is kept
 *    (`tokenHash`, HMAC-SHA256 with the platform pepper, the same keyed hash
 *    the sign-in links use), so a database read cannot be turned into working
 *    links. Lookups go by the hash through a unique index.
 *  - A link is good until `expiresAt`, until it is revoked, or until it is
 *    locked after too many failed charge/save attempts (card testing), and an
 *    invoice or statement link also stops once nothing it covers is owed.
 *  - Every public request must arrive on the storefront the link was issued
 *    for (`fkPortalSiteId`), whose settings decide whether links work at all.
 *
 * A link is created for every send rather than reused: tokens cannot be shown
 * again once issued, and a per-send row records who it went to.
 */
@Index('UK_payment_link_tokenHash', ['tokenHash'], { unique: true })
@Index('IDX_payment_link_client', ['fkClientId'])
@Index('IDX_payment_link_statement', ['fkStatementId'])
@Entity('payment_link', {}, { label: 'Payment Link' })
export class PaymentLinkEntity extends BaseEntity {
	/** See {@link PaymentLinkType}. */
	@Column('varchar', { name: 'linkType', length: 32 })
	public linkType!: PaymentLinkType;

	/**
	 * Keyed hash of the token (hex HMAC-SHA256). The token is shown once, when
	 * the link is created, and is never recoverable from this column.
	 */
	@Column('varchar', { name: 'tokenHash', length: 64 })
	public tokenHash!: string;

	/**
	 * The paying (or saving) account. Invoice links use the invoice's client,
	 * statement links the statement's client; a card saved through the link is
	 * vaulted on this client.
	 */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient?: ClientEntity;

	/**
	 * The company whose brand and storefront the link was issued for: the
	 * invoice's or statement's company, or the client's primary company for a
	 * payment-method link. Null when it could not be resolved.
	 */
	@Column('int', { name: 'fk_company__id', unsigned: true, nullable: true })
	public fkCompanyId!: number | null;

	/**
	 * The storefront the link points at. A public request that arrives on any
	 * other storefront is refused, and this site's settings decide whether the
	 * link works at all.
	 */
	@Column('int', { name: 'fk_portal_site__id', unsigned: true })
	public fkPortalSiteId!: number;

	/**
	 * The stored statement a statement link was sent with. Null for a
	 * statement emailed on demand (those are not stored) and for every other
	 * link type; the invoices the link covers are in payment_link_invoice
	 * either way.
	 */
	@Column('int', { name: 'fk_statement__id', unsigned: true, nullable: true })
	public fkStatementId!: number | null;

	/**
	 * The staff member who created the link (tenant `user._id`). Null for the
	 * links the system adds to automatic emails. Payment-method links notify
	 * this person each time the link is used.
	 */
	@Column('int', { name: 'fk_user__id_creator', unsigned: true, nullable: true })
	public fkUserIdCreator!: number | null;

	/** See {@link PaymentLinkSource}. */
	@Column('varchar', { name: 'source', length: 32 })
	public source!: PaymentLinkSource;

	/** The address the link was emailed to, when it was emailed. Staff-side only. */
	@Column('varchar', { name: 'sentTo', length: 255, nullable: true })
	public sentTo!: string | null;

	@Column('datetime', { name: 'expiresAt' })
	public expiresAt!: Date;

	@Column('datetime', { name: 'revokedAt', nullable: true })
	public revokedAt!: Date | null;

	/** Who revoked it (tenant `user._id`). */
	@Column('int', { name: 'fk_user__id_revoker', unsigned: true, nullable: true })
	public fkUserIdRevoker!: number | null;

	/** Successful payments or saves made through the link. */
	@Column('int', { name: 'useCount', unsigned: true, default: 0 })
	public useCount!: number;

	@Column('datetime', { name: 'lastUsedAt', nullable: true })
	public lastUsedAt!: Date | null;

	/**
	 * Charges or saves the gateway refused through this link. At the cap
	 * (PAYMENT_LINK_MAX_FAILED_ATTEMPTS) the link locks, so a forwarded or
	 * leaked link cannot be used to test card numbers.
	 */
	@Column('int', { name: 'failedAttemptCount', unsigned: true, default: 0 })
	public failedAttemptCount!: number;

	@Column('datetime', { name: 'lockedAt', nullable: true })
	public lockedAt!: Date | null;
}
